import { ChangeEvent, useEffect, useState } from 'react';
import { FormControlLabel, MenuItem, Switch } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import Alert from '@material-ui/lab/Alert';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import TripDTO, { TripEditDateBounds } from '#root/models/TripDTO';
import { GuveryTextField } from '#root/components';
import { GuveryDialog, GuverySpace } from '#root/ui-components';
import { useNotification } from '#root/hooks';
import { tripService } from '#root/services';
import { parseYmd } from '#root/utils';
import styles from './updateDialog.module.scss';

const housingArray = ['Casa propia/familiar', 'Hotel', 'AirBnb', 'No tengo un lugar fijo'];

const YMD = 'yyyy-MM-dd';

/** Las claves son las del API (body del PUT), no las de la columna. */
interface FormValues {
  name: string;
  city: string;
  zipcode: string;
  accommodation_type: string;
  adults_qty: string;
  arrive_date: Date | null;
  delivery_date: Date | null;
  delivery_place: string;
  flag_receive: boolean;
  flag_pickup: boolean;
  flag_no_travel: boolean;
}

const emptyValues: FormValues = {
  name: '',
  city: '',
  zipcode: '',
  accommodation_type: '',
  adults_qty: '',
  arrive_date: null,
  delivery_date: null,
  delivery_place: '',
  flag_receive: false,
  flag_pickup: false,
  flag_no_travel: false,
};

/** El API manda los flags como '0' | '1' (a veces boolean). */
const toBool = (value?: string | number | boolean | null) =>
  value === true || value === 1 || value === '1';

/**
 * `format()` lanza RangeError con una fecha invalida, y el picker entrega una
 * mientras el usuario tipea: se devuelve '' y el submit queda deshabilitado.
 */
const toYmd = (date: Date | null) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return format(date, YMD);
};

const laterDate = (a: Date | null, b: Date | null) => {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
};

/** El API puede mandar numeros donde el form espera texto (zipcode, adultos). */
const toText = (value?: string | number | null) =>
  value === undefined || value === null ? '' : String(value);

const buildFormValues = (trip: TripDTO): FormValues => ({
  name: toText(trip.nombre),
  city: toText(trip.city),
  zipcode: toText(trip.zipcode),
  accommodation_type: toText(trip.accommodation_type),
  adults_qty: toText(trip.adults_qty),
  arrive_date: parseYmd(trip.arrive_date),
  delivery_date: parseYmd(trip.fecha_entrega),
  delivery_place: toText(trip.lugar_entrega),
  flag_receive: toBool(trip.flag_receive),
  flag_pickup: toBool(trip.flag_pickup),
  flag_no_travel: toBool(trip.flag_no_travel),
});

/** Una fecha (ya en "Y-m-d") dentro de la ventana que permite el server. */
const isWithinBounds = (value: string, bounds?: TripEditDateBounds) => {
  if (!bounds) return true;
  if (bounds.min && value < bounds.min) return false;
  if (bounds.max && value > bounds.max) return false;
  return true;
};

interface UpdateDialogProps {
  open?: boolean;
  trip?: TripDTO | null;
  onClose?: () => void;
  /** Recibe el viaje fresco que devuelve el PUT (con is_offerable y edit_policy). */
  onSuccess?: (trip: TripDTO) => void;
}

const UpdateDialog = (props: UpdateDialogProps) => {
  const { open = false, trip = null, onClose, onSuccess } = props;
  const { createMessage } = useNotification();
  const [values, setValues] = useState<FormValues>(emptyValues);
  const [initialValues, setInitialValues] = useState<FormValues>(emptyValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const policy = trip?.edit_policy;
  const mode = policy?.mode;
  const editableFields = policy?.editable_fields ?? [];
  const isFull = mode === 'full';
  const isRestricted = mode === 'restricted';
  const isEditable = isFull || isRestricted;

  const arriveBounds = policy?.bounds?.arrive_date;
  // Asimetria deliberada del contrato: bounds usa claves de COLUMNA
  // (`fecha_entrega`), el body del PUT usa claves de API (`delivery_date`).
  const deliveryBounds = policy?.bounds?.fecha_entrega;
  const arriveMinDate = parseYmd(arriveBounds?.min);
  const arriveMaxDate = parseYmd(arriveBounds?.max);
  // La entrega nunca puede quedar antes del viaje: es invariante del server.
  const deliveryMinDate = laterDate(parseYmd(deliveryBounds?.min), values.arrive_date);
  const deliveryMaxDate = parseYmd(deliveryBounds?.max);

  useEffect(() => {
    if (!open || !trip) return;
    const nextValues = buildFormValues(trip);
    setInitialValues(nextValues);
    setValues(nextValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trip?.id]);

  const handleInputChange = (field: keyof FormValues) => (
    e: ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleDateChange = (field: 'arrive_date' | 'delivery_date') => (date: any) => {
    setValues((prev) => ({ ...prev, [field]: date }));
  };

  // Misma regla de negocio que la creacion: siempre debe quedar activo al menos
  // uno de los dos (recibir compras / recoger en tienda).
  const handleSwitchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setValues((prev) => {
      const next = { ...prev, [name]: checked };
      if (name === 'flag_receive' && !checked) next.flag_pickup = true;
      if (name === 'flag_pickup' && !checked) next.flag_receive = true;
      return next;
    });
  };

  /** Solo los campos que cambiaron Y que el server declara editables. */
  const buildPayload = () => {
    const payload: Record<string, any> = {};
    const put = (key: string, value: any) => {
      if (editableFields.includes(key)) payload[key] = value;
    };

    if (values.name.trim() !== initialValues.name.trim()) {
      put('name', values.name.trim());
    }
    if (values.city.trim() !== initialValues.city.trim()) {
      put('city', values.city.trim());
    }
    if (values.zipcode.trim() !== initialValues.zipcode.trim()) {
      put('zipcode', values.zipcode.trim());
    }
    if (values.accommodation_type !== initialValues.accommodation_type) {
      put('accommodation_type', values.accommodation_type);
    }
    if (values.adults_qty.trim() !== initialValues.adults_qty.trim()) {
      put('adults_qty', values.adults_qty.trim());
    }
    // Una fecha invalida da '' y no se envia: el server rechaza los vacios.
    const arriveYmd = toYmd(values.arrive_date);
    if (arriveYmd && arriveYmd !== toYmd(initialValues.arrive_date)) {
      put('arrive_date', arriveYmd);
    }
    const deliveryYmd = toYmd(values.delivery_date);
    if (deliveryYmd && deliveryYmd !== toYmd(initialValues.delivery_date)) {
      put('delivery_date', deliveryYmd);
    }
    if (values.delivery_place.trim() !== initialValues.delivery_place.trim()) {
      put('delivery_place', values.delivery_place.trim());
    }
    // Los flags viajan como '0' | '1': '0' es significativo, no se filtra.
    if (values.flag_receive !== initialValues.flag_receive) {
      put('flag_receive', values.flag_receive ? '1' : '0');
    }
    if (values.flag_pickup !== initialValues.flag_pickup) {
      put('flag_pickup', values.flag_pickup ? '1' : '0');
    }
    if (values.flag_no_travel !== initialValues.flag_no_travel) {
      put('flag_no_travel', values.flag_no_travel ? '1' : '0');
    }

    return payload;
  };

  const zipcodeError = isFull && values.zipcode.trim() !== '' && !/^\d{5}$/.test(values.zipcode.trim());

  /** El server es la autoridad; esto solo evita pedidos que ya sabemos invalidos. */
  const isFormValid = () => {
    if (!isEditable) return false;

    const arriveYmd = toYmd(values.arrive_date);
    const deliveryYmd = toYmd(values.delivery_date);
    if (!arriveYmd || !deliveryYmd) return false;
    if (deliveryYmd < arriveYmd) return false;
    if (!isWithinBounds(arriveYmd, arriveBounds)) return false;
    if (!isWithinBounds(deliveryYmd, deliveryBounds)) return false;

    if (isFull) {
      // El server rechaza cualquier campo no-flag vacio (blanquearia la columna).
      const required = [
        values.name,
        values.city,
        values.zipcode,
        values.accommodation_type,
        values.adults_qty,
        values.delivery_place,
      ];
      if (required.some((item) => item.trim() === '')) return false;
      if (zipcodeError) return false;
      const adultsQty = Number(values.adults_qty);
      if (!Number.isInteger(adultsQty) || adultsQty < 1) return false;
    }

    return true;
  };

  const hasChanges = Object.keys(buildPayload()).length > 0;
  const canSubmit = isFormValid() && hasChanges && !isSubmitting;

  const handleSubmit = async () => {
    if (!trip || !canSubmit) return;
    const payload = buildPayload();

    setIsSubmitting(true);
    const res = await tripService.update(trip.id, payload);
    setIsSubmitting(false);

    if (res.error) {
      // Mensaje literal del server (estan tipificados en el contrato del PUT).
      createMessage.error(res.message);
      return;
    }
    createMessage.success(res.message);
    if (onSuccess) onSuccess(res.data);
    if (onClose) onClose();
  };

  const datePickers = (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={es}>
      <div className={styles.dates}>
        <KeyboardDatePicker
          disableToolbar
          fullWidth
          inputVariant="outlined"
          format="dd/MM/yyyy"
          label="Fecha de viaje"
          value={values.arrive_date}
          onChange={handleDateChange('arrive_date')}
          minDate={arriveMinDate ?? undefined}
          maxDate={arriveMaxDate ?? undefined}
          InputAdornmentProps={{ position: 'start' }}
          minDateMessage="La fecha no debe ser anterior al mínimo permitido"
          maxDateMessage="La fecha no debe ser posterior al máximo permitido"
        />
        <KeyboardDatePicker
          disableToolbar
          fullWidth
          inputVariant="outlined"
          format="dd/MM/yyyy"
          label="Fecha de entrega"
          value={values.delivery_date}
          onChange={handleDateChange('delivery_date')}
          minDate={deliveryMinDate ?? undefined}
          maxDate={deliveryMaxDate ?? undefined}
          InputAdornmentProps={{ position: 'start' }}
          minDateMessage="La fecha no debe ser anterior a la fecha de viaje"
          maxDateMessage="La fecha no debe ser posterior al máximo permitido"
        />
      </div>
    </MuiPickersUtilsProvider>
  );

  const restrictedContent = (
    <div className={styles.form}>
      <Alert severity="info">
        {`Tienes ${policy?.active_offers_count ?? 0} oferta(s) vigente(s) y ${
          policy?.active_deals_count ?? 0
        } acuerdo(s) en curso: solo puedes mover cada fecha hasta 2 días. Tus ofertas y acuerdos no cambian.`}
      </Alert>
      {datePickers}
    </div>
  );

  const fullContent = (
    <div className={styles.form}>
      <GuverySpace direction="vertical" size="large">
        <GuveryTextField
          id="name"
          label="Nombre del viaje *"
          value={values.name}
          onChange={handleInputChange('name')}
        />
        <div className={styles.row}>
          <GuveryTextField
            id="zipcode"
            label="Zipcode *"
            value={values.zipcode}
            onChange={handleInputChange('zipcode')}
            error={zipcodeError}
            helperText={zipcodeError && '*Debe tener 5 dígitos'}
          />
          <GuveryTextField
            id="city"
            label="Nombre de Ciudad *"
            value={values.city}
            onChange={handleInputChange('city')}
          />
        </div>
        <GuveryTextField
          select
          id="accommodation_type"
          label="Alojamiento en EE.UU"
          value={values.accommodation_type}
          onChange={handleInputChange('accommodation_type')}>
          <MenuItem value="" disabled>
            Alojamiento en EE.UU.
          </MenuItem>
          {housingArray.map((option, index) => (
            <MenuItem key={index} value={option}>
              {option}
            </MenuItem>
          ))}
        </GuveryTextField>
        <GuveryTextField
          id="adults_qty"
          label="Cantidad de adultos"
          type="number"
          value={values.adults_qty}
          onChange={handleInputChange('adults_qty')}
        />
        {datePickers}
        <GuveryTextField
          id="delivery_place"
          label="Distrito(s) de entrega (Lima)"
          value={values.delivery_place}
          onChange={handleInputChange('delivery_place')}
        />
        <div className={styles.switches}>
          <FormControlLabel
            label="Puedo recibir compras."
            control={
              <Switch
                color="primary"
                name="flag_receive"
                checked={values.flag_receive}
                onChange={handleSwitchChange}
              />
            }
          />
          <FormControlLabel
            label="Puedo recoger en tienda."
            control={
              <Switch
                color="primary"
                name="flag_pickup"
                checked={values.flag_pickup}
                onChange={handleSwitchChange}
              />
            }
          />
          <FormControlLabel
            label="Yo no estoy viajando, pero soy él/la responsable."
            control={
              <Switch
                color="primary"
                name="flag_no_travel"
                checked={values.flag_no_travel}
                onChange={handleSwitchChange}
              />
            }
          />
        </div>
      </GuverySpace>
    </div>
  );

  let content = null;
  if (!trip || !policy) {
    // Backend sin `edit_policy` (orden de deploy BE -> FE): no inventamos un
    // formulario que el server va a rechazar.
    content = (
      <p className={styles.desc}>
        No pudimos obtener los permisos de edición de este viaje. Recarga la página e
        inténtalo de nuevo.
      </p>
    );
  } else if (mode === 'none') {
    content = <p className={styles.desc}>Este viaje ya finalizó y no se puede editar.</p>;
  } else if (isRestricted) {
    content = restrictedContent;
  } else {
    content = fullContent;
  }

  return (
    <GuveryDialog
      open={open}
      onClose={onClose}
      title="Modificar Viaje"
      closeText={isEditable ? 'Cancelar' : 'Cerrar'}
      confirmText="Guardar cambios"
      onConfirm={isEditable ? handleSubmit : undefined}
      onConfirmDisable={!canSubmit}>
      <div className={styles.root}>{content}</div>
    </GuveryDialog>
  );
};

export default UpdateDialog;
